import React, { FC, Fragment, useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  DefaultTooltipContent,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { prepareMinMaxRange } from '@app/helpers/lineChartsHelper';
import { InputCheckbox } from '@shared/uikit/input-checkbox';
import useMainGraph from '@pages/Main/hooks/useMainGraph';
import { ListLoader } from '@shared/uikit/loader';
import { IMainIndicatorsFilter } from '@pages/Main/components/Filter';

interface IMultiLinesChartData {
  name: string;
  value?: number;
  globalHashRates?: number;
  hashRates?: number;
  viaBtcHashRates?: number;
  reward?: number;
  lostHashRate?: number;
  increaseHashRate?: number;
  accounts_hashrate?: number;
}

enum DataKeys {
  globalHashRates = 'globalHashRates',
  hashRates = 'hashRates',
  increaseHashRate = 'increaseHashRate',
  reward = 'reward',
  viaBtcHashRates = 'viaBtcHashRates',
  lostHashRate = 'lostHashRate',
}

interface IMultiLinesChartProps {
  filters?: IMainIndicatorsFilter | null;
}

const HostingChart: FC<IMultiLinesChartProps> = ({ filters }) => {
  const KEY_POSTFIX = 'Proportionally';

  const [nominalPower, setNominalPower] = useState<boolean>(false);
  const [actualPower, setActualPower] = useState<boolean>(true);
  const [averageReward, setAverageReward] = useState<boolean>(false);
  const [clientsCount, setClientsCount] = useState<boolean>(false);
  const [lostClientsAndHosting, setLostClientsAndHosting] = useState<boolean>(false);
  const [reward, setReward] = useState<boolean>(false);
  const [commonIncome, setCommonIncome] = useState<boolean>(false);

  const [loadingGraphData, graphData] = useMainGraph(
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    true,
    filters
  );

  // add additional graphData keys, for proportionally display lines on chart
  ((data: IMultiLinesChartData[]) => {
    if (!data.length) {
      return;
    }

    let maxValuesByKey = {};
    let maxValue = 0;

    Object.values(DataKeys).map(key => {
      if (data[0].hasOwnProperty(key)) {
        let values = data.map(item => item[key]);
        // @ts-ignore
        let max = Math.max(...values),
          min = Math.min(...values);

        // @ts-ignore
        maxValuesByKey[key] = max;
        maxValue = maxValue > max ? maxValue : max;
      }
    });

    data.map((item, index) => {
      for (let key in maxValuesByKey) {
        // @ts-ignore
        let multiplier = item[key] / maxValuesByKey[key];
        // @ts-ignore
        item[key + KEY_POSTFIX] = maxValue * multiplier;
      }

      return item;
    });
  })(graphData);

  const prepareDomain = (data: IMultiLinesChartData[]) => {
    let dataMin = 'dataMin';
    let dataMax = 'dataMax';

    if (!data.length) {
      return;
    }

    let minValue = 0,
      maxValue = 0;
    Object.values(DataKeys).map(key => {
      if (data[0].hasOwnProperty(key + KEY_POSTFIX)) {
        // @ts-ignore
        let values = data.map(item => item[key + KEY_POSTFIX]);
        let max = Math.max(...values),
          min = Math.min(...values);

        if (0 === minValue) {
          minValue = min;
        }

        maxValue = maxValue > max ? maxValue : max;
        minValue = minValue > min ? min : minValue;
      }
    });

    if (0 === minValue && 0 === maxValue) return [dataMin, dataMax];

    return prepareMinMaxRange(minValue, maxValue);
  };

  // @ts-ignore
  const CustomTooltip = ({ active, payload }) => {
    const isVisible = active && payload && payload.length;

    return (
      <div
        className="custom-tooltip rounded-[8px] bg-[#211F24] p-[10px]"
        style={{ visibility: isVisible ? 'visible' : 'hidden' }}
      >
        {isVisible &&
          payload.map(
            (
              item: { dataKey: string; color: any; name: any; payload: { [x: string]: any } },
              index: number | string
            ) => {
              let dataKey = item.dataKey.replace(KEY_POSTFIX, '');

              return (
                <Fragment key={index}>
                  <p
                    className="pb-1"
                    style={{ color: item.color }}
                  >{`${item.name} : ${item.payload[dataKey]}`}</p>
                </Fragment>
              );
            }
          )}
      </div>
    );
  };

  return (
    <>
      {!loadingGraphData ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={graphData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs></defs>
              <XAxis dataKey="name" interval={0} tickLine={false} />
              {/*{1 < countValuesKeys ? (*/}
              {/*    <YAxis />*/}
              {/*) : (*/}
              <YAxis width={70} type="number" domain={prepareDomain(graphData)} allowDataOverflow />
              {/*)}*/}
              {152 > graphData.length ? <CartesianGrid opacity={0.1} color="#5E5E5E33" /> : null}
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="natural"
                name={'Дата'}
                dataKey="Дата"
                stroke="white"
                stackId={213}
                fillOpacity={1}
              />
              <Area
                type="linear"
                dataKey="accounts_hashrate"
                name={'Хостинг'}
                unit={' Ph'}
                stroke="#B325F5"
                fillOpacity={1}
                fill="#501A6B"
              />
              <Area
                type="linear"
                dataKey="accounts_accounts"
                name={'Опт'}
                stroke="#FFDC18"
                fillOpacity={1}
                fill="#BCA212"
              />
              <Area
                type="linear"
                dataKey="accounts_clients"
                name={'Розница'}
                stroke="#4598E6"
                fillOpacity={1}
                fill="#2E6396"
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-[20px] gap-y-[5px] px-[55px] pt-[15px]">
            <InputCheckbox
              value={nominalPower}
              onChange={value => {
                setNominalPower(value);

                if (value) {
                  setActualPower(false);
                  setAverageReward(false);
                  setClientsCount(false);
                  setLostClientsAndHosting(false);
                  setReward(false);
                  setCommonIncome(false);
                }
              }}
              label="Общая мощность (номинальная)"
            />

            <InputCheckbox
              value={actualPower}
              onChange={value => {
                setActualPower(value);

                if (value) {
                  setNominalPower(false);
                  setAverageReward(false);
                  setClientsCount(false);
                  setLostClientsAndHosting(false);
                  setReward(false);
                  setCommonIncome(false);
                }
              }}
              label="Общая мощность (фактически)"
            />
            <InputCheckbox
              value={averageReward}
              onChange={value => {
                setAverageReward(value);
                if (value) {
                  setNominalPower(false);
                  setActualPower(false);
                  setClientsCount(false);
                  setLostClientsAndHosting(false);
                  setReward(false);
                  setCommonIncome(false);
                }
              }}
              label="Ср. доходность на кВт"
            />
            <InputCheckbox
              value={clientsCount}
              onChange={value => {
                setClientsCount(value);
                if (value) {
                  setNominalPower(false);
                  setActualPower(false);
                  setAverageReward(false);
                  setLostClientsAndHosting(false);
                  setReward(false);
                  setCommonIncome(false);
                }
              }}
              label="Кол-во клиентов"
            />
            <InputCheckbox
              value={lostClientsAndHosting}
              onChange={value => {
                setLostClientsAndHosting(value);
                if (value) {
                  setNominalPower(false);
                  setActualPower(false);
                  setAverageReward(false);
                  setClientsCount(false);
                  setReward(false);
                  setCommonIncome(false);
                }
              }}
              label="Потеря клиентов и хостингов"
            />
            <InputCheckbox
              value={reward}
              onChange={value => {
                setReward(value);
                if (value) {
                  setNominalPower(false);
                  setActualPower(false);
                  setAverageReward(false);
                  setClientsCount(false);
                  setLostClientsAndHosting(false);
                  setCommonIncome(false);
                }
              }}
              label="Доход (usd и руб оба графика)"
            />
            <InputCheckbox
              value={commonIncome}
              onChange={value => {
                setCommonIncome(value);
                if (value) {
                  setNominalPower(false);
                  setActualPower(false);
                  setAverageReward(false);
                  setClientsCount(false);
                  setLostClientsAndHosting(false);
                  setReward(false);
                }
              }}
              label="Общая выручка"
            />
          </div>
        </>
      ) : (
        <ListLoader loading={true} />
      )}
    </>
  );
};

export default HostingChart;
